import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Tag, Space, Modal, Alert, Descriptions,
  Input, message, Spin, Tooltip, Typography, Row, Col,
} from 'antd';
import {
  FileAddOutlined, CheckCircleOutlined, CloseCircleOutlined,
  ExclamationCircleOutlined, SearchOutlined, ClearOutlined,
} from '@ant-design/icons';
import fiscalService from '../../services/fiscalService';

const { Text } = Typography;

const STATUS_RPS_COLOR = {
  RPS_PENDENTE: 'blue',
  VALIDADO: 'cyan',
  ENVIADO: 'geekblue',
  PROCESSANDO: 'purple',
  EMITIDO: 'green',
  ERRO: 'red',
  CANCELADO: 'default',
  SUBSTITUIDO: 'orange',
};

const FILTROS_VAZIOS = {
  competencia: '',
  competencia_de: '',
  competencia_ate: '',
  id_fatura: '',
  busca: '',
};

const FaturasNfse = () => {
  const [faturas, setFaturas] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  // Filtros aplicados (disparam o fetch)
  const [filtros, setFiltros] = useState(FILTROS_VAZIOS);
  // Rascunho dos inputs (antes de clicar em Buscar)
  const [draft, setDraft] = useState(FILTROS_VAZIOS);

  // Ordenação
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState(null);

  // Modal pré-validação (unitário)
  const [preValidando, setPreValidando] = useState(false);
  const [gerandoRps, setGerandoRps] = useState(false);
  const [validacaoResult, setValidacaoResult] = useState(null);
  const [selectedFatura, setSelectedFatura] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  // Seleção e geração em lote
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [modalLoteVisible, setModalLoteVisible] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [resultadoLote, setResultadoLote] = useState(null);

  const fetchFaturas = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, size: 50 };
      if (filtros.competencia)    params.competencia     = filtros.competencia;
      if (filtros.competencia_de) params.competencia_de  = filtros.competencia_de;
      if (filtros.competencia_ate) params.competencia_ate = filtros.competencia_ate;
      if (filtros.id_fatura)      params.id_fatura       = parseInt(filtros.id_fatura, 10);
      if (filtros.busca)          params.busca           = filtros.busca;
      if (sortBy) {
        params.sort_by  = sortBy;
        params.sort_dir = sortDir || 'desc';
      }
      const resp = await fiscalService.getFaturasElegiveis(params);
      setFaturas(resp.data.items);
      setTotal(resp.data.total);
    } catch (err) {
      const detail = err.response?.data?.detail;
      message.error(typeof detail === 'string' ? detail : 'Erro ao carregar faturas elegíveis.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, filtros, sortBy, sortDir]);

  useEffect(() => {
    fetchFaturas();
  }, [fetchFaturas]);

  const handleBuscar = () => {
    setFiltros({ ...draft });
    setPage(1);
  };

  const handleLimpar = () => {
    setDraft(FILTROS_VAZIOS);
    setFiltros(FILTROS_VAZIOS);
    setSortBy(null);
    setSortDir(null);
    setPage(1);
  };

  const handleDraft = (campo, valor) => {
    setDraft(prev => {
      const novo = { ...prev, [campo]: valor };

      if (campo === 'competencia' && valor) {
        novo.competencia_de = '';
        novo.competencia_ate = '';
      } else if (
        (campo === 'competencia_de' || campo === 'competencia_ate') &&
        valor
      ) {
        novo.competencia = '';
      }

      return novo;
    });
  };

  const handleTableChange = (_pagination, _filters, sorter) => {
    const novoSortBy  = sorter?.columnKey && sorter?.order ? sorter.columnKey : null;
    const novoSortDir = sorter?.order === 'ascend' ? 'asc' : (sorter?.order === 'descend' ? 'desc' : null);
    const sortouMudou = novoSortBy !== sortBy || novoSortDir !== sortDir;
    setSortBy(novoSortBy);
    setSortDir(novoSortDir);
    if (sortouMudou) {
      setPage(1);
    } else {
      setPage(_pagination.current);
    }
  };

  const handlePreValidar = async (fatura) => {
    setSelectedFatura(fatura);
    setValidacaoResult(null);
    setModalVisible(true);
    setPreValidando(true);
    try {
      const resp = await fiscalService.preValidar(fatura.id_fatura);
      setValidacaoResult(resp.data);
    } catch {
      message.error('Erro ao pré-validar fatura.');
    } finally {
      setPreValidando(false);
    }
  };

  const handleGerarRps = async () => {
    if (!selectedFatura) return;
    setGerandoRps(true);
    try {
      await fiscalService.gerarRps({ id_fatura: selectedFatura.id_fatura });
      message.success(`RPS gerado com sucesso para fatura ${selectedFatura.id_fatura}.`);
      setModalVisible(false);
      await fetchFaturas();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.erros) {
        detail.erros.forEach(e => message.error(e));
      } else {
        message.error(typeof detail === 'string' ? detail : 'Erro ao gerar RPS.');
      }
    } finally {
      setGerandoRps(false);
    }
  };

  const sortOrder = (key) => sortBy === key ? (sortDir === 'asc' ? 'ascend' : 'descend') : null;

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => {
      if (keys.length > 100) {
        message.warning('Máximo de 100 faturas por lote.');
        return;
      }
      setSelectedRowKeys(keys);
    },
    getCheckboxProps: (row) => ({
      disabled: row.rps_existente && row.status_rps !== 'CANCELADO',
    }),
  };

  const handleGerarLote = async () => {
    setGerando(true);
    try {
      const resp = await fiscalService.gerarRpsLote({ fatura_ids: selectedRowKeys });
      setResultadoLote(resp.data);
      setSelectedRowKeys([]);
      await fetchFaturas();
    } catch (err) {
      const detail = err.response?.data?.detail;
      message.error(typeof detail === 'string' ? detail : 'Erro ao processar o lote.');
      setModalLoteVisible(false);
    } finally {
      setGerando(false);
    }
  };

  const fecharModalLote = () => {
    setModalLoteVisible(false);
    setResultadoLote(null);
  };

  const columns = [
    {
      title: 'Fatura',
      dataIndex: 'id_fatura',
      key: 'fatura',
      width: 80,
      sorter: true,
      sortOrder: sortOrder('fatura'),
      render: (v) => <Text strong>#{v}</Text>,
    },
    {
      title: 'Aluno',
      dataIndex: 'nome_aluno',
      key: 'aluno',
      sorter: true,
      sortOrder: sortOrder('aluno'),
    },
    {
      title: 'Responsável',
      dataIndex: 'tomador_nome',
      key: 'responsavel',
      sorter: true,
      sortOrder: sortOrder('responsavel'),
      render: (v, row) => (
        <span>
          {v || <Text type="warning">Não identificado</Text>}
          {row.tomador_cpf && <Text type="secondary"> — {row.tomador_cpf}</Text>}
        </span>
      ),
    },
    {
      title: 'Competência',
      dataIndex: 'competencia',
      key: 'competencia',
      width: 110,
      sorter: true,
      sortOrder: sortOrder('competencia'),
    },
    {
      title: 'Valor (R$)',
      dataIndex: 'valor_total',
      key: 'valor',
      width: 110,
      align: 'right',
      sorter: true,
      sortOrder: sortOrder('valor'),
      render: (v) => parseFloat(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
    },
    {
      title: 'Pgto',
      dataIndex: 'data_pagamento',
      key: 'pagamento',
      width: 100,
      sorter: true,
      sortOrder: sortOrder('pagamento'),
      render: (v) => v || '—',
    },
    {
      title: 'RPS',
      key: 'rps',
      width: 120,
      render: (_, row) => {
        if (!row.rps_existente) return <Tag color="default">Sem RPS</Tag>;
        return <Tag color={STATUS_RPS_COLOR[row.status_rps] || 'default'}>{row.status_rps}</Tag>;
      },
    },
    {
      title: 'Ações',
      key: 'acoes',
      width: 100,
      render: (_, row) => (
        <Space>
          <Tooltip title={row.rps_existente ? 'Já possui RPS ativo' : 'Gerar RPS'}>
            <Button
              size="small"
              type="primary"
              icon={<FileAddOutlined />}
              disabled={row.rps_existente && row.status_rps !== 'CANCELADO'}
              onClick={() => handlePreValidar(row)}
            >
              RPS
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const temFiltroAtivo = Object.values(filtros).some(v => v !== '');

  return (
    <div>
      <h2 style={{ marginBottom: 16 }}>Emissão de NFS-e — Faturas Elegíveis</h2>

      {/* Filtros */}
      <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6, padding: '12px 16px', marginBottom: 16 }}>
        <Row gutter={[8, 8]} align="bottom">
          <Col xs={24} sm={8} md={5}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>Competência exata</div>
            <Input
              placeholder="YYYY-MM"
              value={draft.competencia}
              onChange={e => handleDraft('competencia', e.target.value)}
              onPressEnter={handleBuscar}
              maxLength={7}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>Comp. inicial</div>
            <Input
              placeholder="YYYY-MM"
              value={draft.competencia_de}
              onChange={e => handleDraft('competencia_de', e.target.value)}
              onPressEnter={handleBuscar}
              maxLength={7}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>Comp. final</div>
            <Input
              placeholder="YYYY-MM"
              value={draft.competencia_ate}
              onChange={e => handleDraft('competencia_ate', e.target.value)}
              onPressEnter={handleBuscar}
              maxLength={7}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={6} md={3}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>Nº fatura</div>
            <Input
              placeholder="Ex.: 42"
              value={draft.id_fatura}
              onChange={e => handleDraft('id_fatura', e.target.value)}
              onPressEnter={handleBuscar}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={8} md={5}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>Aluno (busca por nome)</div>
            <Input
              placeholder="Nome do aluno"
              value={draft.busca}
              onChange={e => handleDraft('busca', e.target.value)}
              onPressEnter={handleBuscar}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleBuscar}>
                Buscar
              </Button>
              {temFiltroAtivo && (
                <Button icon={<ClearOutlined />} onClick={handleLimpar}>
                  Limpar
                </Button>
              )}
            </Space>
          </Col>
        </Row>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, minHeight: 32 }}>
        <Text type="secondary">
          {selectedRowKeys.length > 0 ? `${selectedRowKeys.length} fatura(s) selecionada(s)` : ''}
        </Text>
        <Button
          type="primary"
          icon={<FileAddOutlined />}
          disabled={selectedRowKeys.length === 0}
          onClick={() => setModalLoteVisible(true)}
        >
          Gerar RPSs selecionados{selectedRowKeys.length > 0 ? ` (${selectedRowKeys.length})` : ''}
        </Button>
      </div>

      <Table
        rowKey="id_fatura"
        rowSelection={rowSelection}
        dataSource={faturas}
        columns={columns}
        loading={loading}
        onChange={handleTableChange}
        pagination={{
          current: page,
          pageSize: 50,
          total,
          onChange: (p) => setPage(p),
          showTotal: (t) => `${t} faturas elegíveis`,
        }}
        scroll={{ x: 900 }}
      />

      {/* Modal de geração em lote */}
      <Modal
        title="Geração de RPSs em Lote"
        open={modalLoteVisible}
        onCancel={fecharModalLote}
        footer={
          resultadoLote ? (
            <Button onClick={fecharModalLote}>Fechar</Button>
          ) : [
            <Button key="cancelar" onClick={fecharModalLote} disabled={gerando}>
              Cancelar
            </Button>,
            <Button
              key="confirmar"
              type="primary"
              loading={gerando}
              disabled={gerando}
              onClick={handleGerarLote}
            >
              Confirmar
            </Button>,
          ]
        }
        width={640}
      >
        {!resultadoLote && !gerando && (
          <div>
            <p>Deseja gerar os RPSs selecionados?</p>
            <p><strong>Quantidade:</strong> {selectedRowKeys.length} registros.</p>
            <Alert
              type="info"
              showIcon
              message="Nenhuma NFS-e será enviada à Prefeitura nesta etapa. Os RPSs serão criados com status RPS_PENDENTE."
              style={{ marginTop: 12 }}
            />
          </div>
        )}

        {gerando && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin tip="Gerando RPSs..." />
          </div>
        )}

        {resultadoLote && !gerando && (
          <div>
            <Space style={{ marginBottom: 12 }}>
              <Tag color="blue">Solicitados: {resultadoLote.total_solicitado}</Tag>
              <Tag color="green">Sucesso: {resultadoLote.total_sucesso}</Tag>
              {resultadoLote.total_falha > 0 && (
                <Tag color="red">Falhas: {resultadoLote.total_falha}</Tag>
              )}
            </Space>

            {resultadoLote.total_falha > 0 && (
              <Table
                size="small"
                rowKey="id_fatura"
                dataSource={resultadoLote.resultados.filter(r => r.status === 'ERRO')}
                columns={[
                  { title: 'Fatura', dataIndex: 'id_fatura', width: 80 },
                  { title: 'Código', dataIndex: 'codigo', width: 180 },
                  { title: 'Mensagem', dataIndex: 'mensagem' },
                ]}
                pagination={false}
              />
            )}

            {resultadoLote.total_falha === 0 && (
              <Alert type="success" showIcon message="Todos os RPSs foram gerados com sucesso." />
            )}
          </div>
        )}
      </Modal>

      {/* Modal de pré-validação unitária */}
      <Modal
        title={selectedFatura ? `Pré-validação — Fatura #${selectedFatura.id_fatura}` : 'Pré-validação'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="cancelar" onClick={() => setModalVisible(false)}>Fechar</Button>,
          <Button
            key="gerar"
            type="primary"
            icon={<FileAddOutlined />}
            loading={gerandoRps}
            disabled={!validacaoResult?.elegivel}
            onClick={handleGerarRps}
          >
            Confirmar Geração de RPS
          </Button>,
        ]}
        width={640}
      >
        {preValidando && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin tip="Validando..." />
          </div>
        )}

        {validacaoResult && !preValidando && (
          <>
            <Alert
              type={validacaoResult.elegivel ? 'success' : 'error'}
              icon={validacaoResult.elegivel ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
              showIcon
              message={validacaoResult.elegivel ? 'Fatura elegível para emissão de NFS-e' : 'Fatura não elegível'}
              style={{ marginBottom: 16 }}
            />

            {validacaoResult.erros?.length > 0 && (
              <Alert
                type="error"
                message="Erros encontrados"
                description={
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    {validacaoResult.erros.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                }
                style={{ marginBottom: 12 }}
              />
            )}

            {validacaoResult.avisos?.length > 0 && (
              <Alert
                type="warning"
                icon={<ExclamationCircleOutlined />}
                showIcon
                message="Avisos"
                description={
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    {validacaoResult.avisos.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                }
                style={{ marginBottom: 12 }}
              />
            )}

            <Descriptions size="small" bordered column={2} title="Gates de Elegibilidade">
              {Object.entries(validacaoResult.gates || {}).map(([gate, ok]) => (
                <Descriptions.Item key={gate} label={gate}>
                  <Tag color={ok ? 'success' : 'error'}>{ok ? 'OK' : 'FALHOU'}</Tag>
                </Descriptions.Item>
              ))}
            </Descriptions>
          </>
        )}
      </Modal>
    </div>
  );
};

export default FaturasNfse;
